import { Grid, Stack, Typography } from "@mui/material";
import React from "react";
import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import { JsonPathFunction } from "models/JsonPathFunction";
import {
    JsonSchema,
    JsonSchemaArray,
    JsonSchemaObject,
    JsonSchemaRoot,
} from "./types";
import {
    JsonPathFunctionSegment,
    JsonPathSegment,
    jsonPathToSegments,
    segmentsToJsonPath,
} from "./utils";

type PropertyOption = {
    id: string;
    name: string;
};

type ArrayIndexOption = {
    id: number;
    name: string;
};

type FunctionOption = {
    id: string;
    name: string;
};

type PickerStep =
    | {
          kind: "array";
          stepIndex: number;
          selectedIndex?: number;
      }
    | {
          kind: "object";
          stepIndex: number;
          options: PropertyOption[];
          selectedProperty?: string;
      }
    | {
          kind: "function";
          stepIndex: number;
          options: FunctionOption[];
          selectedFunctionName?: string;
      };

interface JsonSchemaPathDropdownPickerProps {
    schema: JsonSchemaRoot;
    selectedFieldPath: string | null;
    onSelectFieldPath: (path: string | null) => void;
    availableFunctions?: JsonPathFunction[];
}

const ARRAY_INDEX_OPTIONS: ArrayIndexOption[] = Array.from(
    { length: 10 },
    (_, index) => ({
        id: index,
        name: `${index + 1}`,
    }),
);

function isLeafNode(schema: JsonSchema) {
    return ["string", "number", "integer", "boolean"].includes(schema.type);
}

function getPropertyOptions(schema: JsonSchemaObject): PropertyOption[] {
    return Object.entries(schema.properties ?? {})
        .map(([propertyName, propertySchema]) => ({
            id: propertyName,
            name: propertySchema.title ?? propertyName,
        }))
        .sort((a, b) =>
            a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
        );
}

function getApplicableFunctions(
    schema: JsonSchema | undefined,
    availableFunctions: JsonPathFunction[],
): JsonPathFunction[] {
    if (!schema?.type) return [];
    return availableFunctions.filter((fn) =>
        fn.applicableTypes.includes(schema.type),
    );
}

function toFunctionOptions(functions: JsonPathFunction[]): FunctionOption[] {
    return functions.map((fn) => ({ id: fn.name, name: fn.label }));
}

function isFunctionSegment(
    segment: JsonPathSegment,
): segment is JsonPathFunctionSegment {
    return (
        typeof segment === "object" &&
        segment !== null &&
        (segment as JsonPathFunctionSegment).kind === "function"
    );
}

function buildPickerState(
    schema: JsonSchemaRoot,
    segments: JsonPathSegment[],
    availableFunctions: JsonPathFunction[],
) {
    const steps: PickerStep[] = [];
    const resolvedSegments: JsonPathSegment[] = [];
    let currentSchema: JsonSchema | undefined = schema;
    let functionTerminated = false;

    while (currentSchema) {
        const segmentAtCurrentStep = segments[resolvedSegments.length];

        // A function segment always terminates the path with a trailing item-method.
        if (segmentAtCurrentStep && isFunctionSegment(segmentAtCurrentStep)) {
            const applicable = getApplicableFunctions(
                currentSchema,
                availableFunctions,
            );
            steps.push({
                kind: "function",
                stepIndex: steps.length,
                options: toFunctionOptions(applicable),
                selectedFunctionName: segmentAtCurrentStep.name,
            });
            resolvedSegments.push(segmentAtCurrentStep);
            functionTerminated = true;
            break;
        }

        if (currentSchema.type === "array") {
            const selectedIndex =
                typeof segmentAtCurrentStep === "number"
                    ? segmentAtCurrentStep
                    : undefined;

            steps.push({
                kind: "array",
                stepIndex: steps.length,
                selectedIndex,
            });

            if (selectedIndex === undefined) {
                break;
            }

            resolvedSegments.push(selectedIndex);
            currentSchema = (currentSchema as JsonSchemaArray).items;
            continue;
        }

        if (currentSchema.type === "object") {
            const options = getPropertyOptions(
                currentSchema as JsonSchemaObject,
            );
            const selectedProperty =
                typeof segmentAtCurrentStep === "string" &&
                !!(currentSchema as JsonSchemaObject).properties?.[
                    segmentAtCurrentStep
                ]
                    ? segmentAtCurrentStep
                    : undefined;

            steps.push({
                kind: "object",
                stepIndex: steps.length,
                options,
                selectedProperty,
            });

            if (!selectedProperty) {
                break;
            }

            resolvedSegments.push(selectedProperty);
            currentSchema = (currentSchema as JsonSchemaObject).properties?.[
                selectedProperty
            ];
            continue;
        }

        break;
    }

    // Trailing-function usage (e.g. `.size()`, `.double()`) is disabled for now: SQLJsonUtilities
    // was removed from AcxHelpers in favor of evaluating jsonpath directly in Postgres, and that
    // change has not shipped yet. Re-enable this tail function-step offering once it does.
    // if (!functionTerminated) {
    //     const applicable = getApplicableFunctions(
    //         currentSchema,
    //         availableFunctions,
    //     );
    //     if (applicable.length > 0) {
    //         steps.push({
    //             kind: "function",
    //             stepIndex: steps.length,
    //             options: toFunctionOptions(applicable),
    //             selectedFunctionName: undefined,
    //         });
    //     }
    // }

    const reachedPrimitiveLeaf =
        !!currentSchema &&
        isLeafNode(currentSchema) &&
        resolvedSegments.length > 0;

    return {
        steps,
        resolvedSegments,
        isComplete: functionTerminated || reachedPrimitiveLeaf,
    };
}

export default function JsonSchemaPathDropdownPicker({
    schema,
    selectedFieldPath,
    onSelectFieldPath,
    availableFunctions = [],
}: JsonSchemaPathDropdownPickerProps) {
    const [draftSegments, setDraftSegments] = React.useState<JsonPathSegment[]>(
        () => (selectedFieldPath ? jsonPathToSegments(selectedFieldPath) : []),
    );
    const lastEmittedPathRef = React.useRef<string | null>(null);

    React.useEffect(() => {
        if (selectedFieldPath === lastEmittedPathRef.current) {
            return;
        }

        if (selectedFieldPath === null) {
            setDraftSegments([]);
            return;
        }

        setDraftSegments(jsonPathToSegments(selectedFieldPath));
    }, [selectedFieldPath]);

    const pickerState = React.useMemo(
        () => buildPickerState(schema, draftSegments, availableFunctions),
        [draftSegments, schema, availableFunctions],
    );
    const draftFieldPath = React.useMemo(
        () =>
            pickerState.isComplete
                ? segmentsToJsonPath(pickerState.resolvedSegments)
                : null,
        [pickerState.isComplete, pickerState.resolvedSegments],
    );
    const isAwaitingExternalSync =
        selectedFieldPath !== lastEmittedPathRef.current &&
        ((selectedFieldPath === null && draftSegments.length > 0) ||
            (selectedFieldPath !== null &&
                draftFieldPath !== selectedFieldPath));

    React.useEffect(() => {
        if (isAwaitingExternalSync) {
            return;
        }

        const nextFieldPath = draftFieldPath;

        lastEmittedPathRef.current = nextFieldPath;

        if (nextFieldPath !== selectedFieldPath) {
            onSelectFieldPath(nextFieldPath);
        }
    }, [
        draftFieldPath,
        isAwaitingExternalSync,
        onSelectFieldPath,
        selectedFieldPath,
    ]);

    const updateStep = React.useCallback(
        (stepIndex: number, value?: JsonPathSegment) => {
            let nextSegments: JsonPathSegment[] =
                pickerState.resolvedSegments.slice(0, stepIndex);
            if (value !== undefined) {
                nextSegments.push(value);
            }
            setDraftSegments(nextSegments);
        },
        [pickerState.resolvedSegments],
    );

    return (
        <Stack
            spacing={1}
            sx={{
                border: (theme) => `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                padding: 1.5,
                width: "100%",
            }}
        >
            <Typography>Select Field Path</Typography>
            <Grid container spacing={1}>
                {pickerState.steps.map((step) => {
                    if (step.kind === "array") {
                        const selectedIndexOption = ARRAY_INDEX_OPTIONS.find(
                            (option) => option.id === step.selectedIndex,
                        );

                        return (
                            <Grid item xs={12} key={`array-${step.stepIndex}`}>
                                <AcxSelectSingle
                                    options={ARRAY_INDEX_OPTIONS}
                                    id={`json-path-array-index-${step.stepIndex}`}
                                    inputLabel="Array Element"
                                    labelField="name"
                                    valueField="id"
                                    defaultValue={selectedIndexOption}
                                    onChange={(value) =>
                                        updateStep(step.stepIndex, value.id)
                                    }
                                    helperText="Select element 1-10 for this array step."
                                />
                            </Grid>
                        );
                    }

                    if (step.kind === "function") {
                        const selectedFunctionOption = step.options.find(
                            (option) => option.id === step.selectedFunctionName,
                        );

                        return (
                            <Grid
                                item
                                xs={12}
                                key={`function-${step.stepIndex}`}
                            >
                                <AcxSelectSingle
                                    options={step.options}
                                    id={`json-path-function-${step.stepIndex}`}
                                    inputLabel="Function (optional)"
                                    labelField="name"
                                    valueField="id"
                                    enableNoSelection
                                    defaultValue={selectedFunctionOption}
                                    onChange={(value) => {
                                        updateStep(
                                            step.stepIndex,
                                            value
                                                ? {
                                                      kind: "function",
                                                      name: value.id,
                                                  }
                                                : undefined,
                                        );
                                    }}
                                    helperText="Optional. Apply a trailing function to the value at this path."
                                />
                            </Grid>
                        );
                    }

                    const selectedPropertyOption = step.options.find(
                        (option) => option.id === step.selectedProperty,
                    );

                    return (
                        <Grid item xs={12} key={`object-${step.stepIndex}`}>
                            <AcxSelectSingle
                                options={step.options}
                                id={`json-path-property-${step.stepIndex}`}
                                inputLabel="Property"
                                labelField="name"
                                valueField="id"
                                defaultValue={selectedPropertyOption}
                                onChange={(value) =>
                                    updateStep(step.stepIndex, value.id)
                                }
                            />
                        </Grid>
                    );
                })}
            </Grid>
        </Stack>
    );
}
